import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/lib/hooks/useToast'
import {
  tenantService,
  type Tenant,
  type UpdateTenantData,
} from '@/lib/services/tenantService'
import { Loader2, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { getRoles } from '@/lib/services/rbacService'

interface EditTenantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tenant: Tenant
  onSuccess: () => void
}

/**
 * Dialog for editing an existing tenant whitelist entry
 * Includes role mapping configuration
 */
export function EditTenantDialog({ open, onOpenChange, tenant, onSuccess }: EditTenantDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()

  const [formData, setFormData] = useState<UpdateTenantData>({
    tenantName: tenant.tenantName,
    domain: tenant.domain ?? '',
    notes: tenant.notes ?? '',
  })

  // Fetch available Crisis Buddy roles for display only
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => getRoles(),
  })

  const availableRoles = rolesData?.roles || []

  const updateMutation = useMutation({
    mutationFn: (data: UpdateTenantData) => tenantService.update(tenant.id, data),
    onSuccess: () => {
      toast({
        title: t('admin.tenants.updateSuccess', 'Tenant Updated'),
        description: t(
          'admin.tenants.updateSuccessDescription',
          'Tenant details have been updated.'
        ),
        variant: 'success',
      })
      onSuccess()
      onOpenChange(false)
    },
    onError: (error: Error) => {
      toast({
        title: t('admin.tenants.updateError', 'Update Failed'),
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await updateMutation.mutateAsync(formData)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('admin.tenants.editTenant', 'Edit Tenant')}</DialogTitle>
          <DialogDescription>
            {t(
              'admin.tenants.editTenantDescription',
              'Update tenant details and configure role mappings.'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tenantName">{t('admin.tenants.tenantName', 'Tenant Name')} *</Label>
              <Input
                id="tenantName"
                name="tenantName"
                value={formData.tenantName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="domain">{t('admin.tenants.domain', 'Primary Domain')} *</Label>
              <Input
                id="domain"
                name="domain"
                value={formData.domain}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('admin.tenants.notes', 'Notes')}</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
          </div>

          {/* Fixed Role Mapping Convention Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('admin.tenants.roleMapping', 'Role Mapping')}
              </CardTitle>
              <CardDescription>
                {t(
                  'admin.tenants.fixedRoleMappingInfo',
                  'Role mapping uses a fixed convention. All Entra app roles must follow the pattern "crisisbuddy.<slug>"'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t(
                    'admin.tenants.fixedMappingExplanation',
                    'Entra app roles are automatically mapped based on a fixed convention: crisisbuddy.<slug> → <slug>. For example, "crisisbuddy.admin" becomes "admin".'
                  )}
                </AlertDescription>
              </Alert>

              <div className="bg-muted p-4 rounded space-y-3">
                <p className="text-sm font-medium">
                  {t('admin.tenants.requiredAppRoles', 'Required Entra App Roles')}:
                </p>
                <div className="space-y-2">
                  {availableRoles?.map(role => (
                    <div key={role.id} className="flex items-center justify-between text-sm">
                      <code className="bg-background px-2 py-1 rounded font-mono text-xs">
                        crisisbuddy.{role.slug}
                      </code>
                      <span className="text-muted-foreground">→</span>
                      <Badge variant="secondary">{role.name}</Badge>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {t(
                    'admin.tenants.setupInstructions',
                    'Configure these app role names in your Entra app manifest. No configuration needed here.'
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              {t('admin.tenants.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('admin.tenants.saveChanges', 'Save Changes')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
